import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Container,
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Button,
  Grid
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Store as StoreIcon,
  History as HistoryIcon,
  SwapHoriz as TransferIcon,
  EmojiEvents as EarnIcon,
  CurrencyExchange as ExchangeIcon
} from '@mui/icons-material';
import MarketplaceBrowser from '../../../components/skillcoin/member/MarketplaceBrowser';
import TransferTokensModal from '../../../components/skillcoin/member/TransferTokensModal';
import ExchangeTokensModal from '../../../components/skillcoin/member/ExchangeTokensModal';
import TokenTransactionHistory from '../../../components/skillcoin/member/TokenTransactionHistory';
import TokenWalletCard from '../../../components/skillcoin/member/TokenWalletCard';
import TokenMarketplace from '../../../components/skillcoin/member/TokenMarketplace';
import EarningOpportunities from '../../../components/skillcoin/member/EarningOpportunities';
import TokenHistoryExplorer from '../../../components/skillcoin/member/TokenHistoryExplorer';
import { tokenWalletService } from '../../../services/skillcoinService';

const MemberSkillCoinDashboardPage = () => {
  const { tab = 'wallet' } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState(tab);
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [exchangeModalOpen, setExchangeModalOpen] = useState(false);
  const [transactionStats, setTransactionStats] = useState({
    earned: 0,
    spent: 0,
    sent: 0,
    netBalance: 0,
    totalTransactions: 0
  });

  useEffect(() => {
    fetchWalletData();
  }, []);

  useEffect(() => {
    setCurrentTab(tab);
  }, [tab]);

  useEffect(() => {
    if (Array.isArray(transactions) && transactions.length > 0) {
      calculateTransactionStats(transactions);
    }
  }, [transactions]);

  const calculateTransactionStats = (transactionData) => {
    const stats = transactionData.reduce((acc, tx) => {
      const amount = parseFloat(tx.amount) || 0;
      const type = (tx.type || '').toLowerCase();

      if (type === 'earn') {
        const description = (tx.description || '').toLowerCase();
        if (description.includes('manual credit') || 
            description.includes('manualcredit') || 
            description.includes('credit adjustment') ||
            description.includes('manual') ||
            (tx.metadata && tx.metadata.isManual)) {
          return acc;
        }
        acc.earned += amount;
      } else if (type === 'transfer_in') {
        acc.earned += amount;
      } else if (type === 'spend') {
        acc.spent += amount;
      } else if (type === 'transfer_out') {
        acc.sent += amount;
      }

      return acc;
    }, { earned: 0, spent: 0, sent: 0 });
    
    stats.netBalance = stats.earned - stats.spent - stats.sent;
    stats.totalTransactions = transactionData.length;
    
    setTransactionStats(stats);
  };

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      // Check if getWallets function exists
      if (typeof tokenWalletService.getWallets !== 'function') {
        console.error('tokenWalletService.getWallets is not implemented');
        setWallets([]);
        setTransactions([]);
        setError('Token wallet service is not fully implemented. Some features may not work correctly.');
        return;
      }

      const walletData = await tokenWalletService.getWallets();
      setWallets(walletData || []);

      if (walletData && walletData.length > 0) {
        // Check if getTransactions function exists
        if (typeof tokenWalletService.getTransactions !== 'function') {
          console.error('tokenWalletService.getTransactions is not implemented');
          setTransactions([]);
          setError('Transaction history service is not fully implemented. Some features may not work correctly.');
          return;
        }

        try {
          const transactionData = await tokenWalletService.getTransactions(walletData[0].tokenId, { limit: 1000 });
          const allTransactions = transactionData?.data || [];
          setTransactions(allTransactions);
          calculateTransactionStats(allTransactions);
        } catch (txError) {
          console.error('Error fetching transactions:', txError);
          setTransactions([]);
          calculateTransactionStats([]);
        }
      }
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setError('Failed to load wallet data. Some features may be unavailable.');
      setWallets([]);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    const tabMapping = {
      0: 'wallet',
      1: 'marketplace',
      2: 'history',
      3: 'transfer',
      4: 'earn'
    };

    const newTab = tabMapping[newValue];
    setCurrentTab(newTab);
    navigate(`/member/skillcoin/${newTab}`);
  };

  const getTabIndex = () => {
    const tabMapping = {
      'wallet': 0,
      'marketplace': 1,
      'history': 2,
      'transfer': 3,
      'earn': 4
    };
    return tabMapping[currentTab] || 0;
  };

  const handleTransfer = async (transferData) => {
    try {
      console.log('Transfer data received:', transferData);

      const response = await tokenWalletService.transferTokens({
        recipientMemberId: transferData.recipientMemberId,
        tokenId: wallets[0]?.tokenId,
        amount: parseFloat(transferData.amount),
        description: transferData.note || 'Token transfer'
      });

      await fetchWalletData();
      setTransferModalOpen(false);

      const recipientName = transferData.recipientName || 'the recipient';
      const amount = parseFloat(transferData.amount).toFixed(2);
      const tokenSymbol = wallets[0]?.token?.symbol || 'tokens';

      enqueueSnackbar(`Successfully transferred ${amount} ${tokenSymbol} to ${recipientName}`, {
        variant: 'success',
        autoHideDuration: 5000,
      });

      return response;
    } catch (error) {
      console.error('Error during transfer:', error);

      enqueueSnackbar(`Transfer failed: ${error.message || 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 5000,
      });

      throw error;
    }
  };

  const handleExchange = async (exchangeData) => {
    try {
      console.log('Exchange data received:', exchangeData);

      // Check if exchangeTokens function exists
      if (typeof tokenWalletService.exchangeTokens !== 'function') {
        throw new Error('Token exchange functionality is not implemented yet.');
      }

      const response = await tokenWalletService.exchangeTokens({
        tokenId: wallets[0]?.tokenId,
        amount: parseFloat(exchangeData.amount),
        direction: exchangeData.direction,
        payoutMethod: exchangeData.payoutMethod,
        recipientId: exchangeData.recipientId,
        description: `Token exchange: ${exchangeData.direction === 'to_currency' ? 'tokens to cash' : 'cash to tokens'}`
      });

      await fetchWalletData();
      setExchangeModalOpen(false);

      const amount = parseFloat(exchangeData.amount).toFixed(2);
      const tokenSymbol = wallets[0]?.token?.symbol || 'tokens';
      
      if (exchangeData.direction === 'to_currency') {
        enqueueSnackbar(`Successfully exchanged ${amount} ${tokenSymbol} to cash`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
      } else {
        enqueueSnackbar(`Successfully purchased ${amount} ${tokenSymbol} with cash`, {
          variant: 'success',
          autoHideDuration: 5000,
        });
      }

      return response;
    } catch (error) {
      console.error('Error during exchange:', error);

      enqueueSnackbar(`Exchange failed: ${error.message || 'Unknown error'}`, {
        variant: 'error',
        autoHideDuration: 5000,
      });

      throw error;
    }
  };

  const WalletTab = () => {
    const wallet = wallets.length > 0 ? wallets[0] : null;

    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TokenWalletCard
              wallet={wallet}
              loading={loading}
              transactionStats={transactionStats}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>Quick Actions</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="contained"
                    fullWidth
                    color="primary"
                    onClick={() => navigate('/member/skillcoin/marketplace')}
                    startIcon={<StoreIcon />}
                  >
                    Shop Marketplace
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setTransferModalOpen(true)}
                    startIcon={<TransferIcon />}
                  >
                    Transfer Tokens
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setExchangeModalOpen(true)}
                    startIcon={<ExchangeIcon />}
                    color="secondary"
                  >
                    Exchange Tokens
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <HistoryIcon sx={{ mr: 1 }} />
                Recent Transactions
              </Typography>

              {transactions.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                  No transactions yet
                </Typography>
              ) : (
                <TokenTransactionHistory
                  tokenId={wallet?.tokenId}
                  limit={5}
                  transactions={transactions.slice(0, 5)}
                  transactionStats={transactionStats}
                />
              )}

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/member/skillcoin/history')}
                >
                  View All Transactions
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const TransactionHistoryTab = () => {
    // Add a wrapper function to handle missing getHistoricalTokenTransactions
    const safeGetHistoricalData = async (period) => {
      // Check if the function exists
      if (typeof tokenWalletService.getHistoricalTokenTransactions !== 'function') {
        console.warn('getHistoricalTokenTransactions is not implemented, returning mock data');
        
        // Return mock data structure
        return {
          earnings: Array(12).fill().map((_, i) => ({ 
            date: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i], 
            value: 0 
          })),
          spending: Array(12).fill().map((_, i) => ({ 
            date: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i], 
            value: 0 
          }))
        };
      }
      
      try {
        return await tokenWalletService.getHistoricalTokenTransactions(period);
      } catch (error) {
        console.error('Error fetching historical data:', error);
        // Return mock data on error
        return {
          earnings: [],
          spending: []
        };
      }
    };
    
    return (
      <TokenHistoryExplorer
        initialTransactions={transactions}
        initialStats={transactionStats}
        fetchHistoricalData={safeGetHistoricalData}
      />
    );
  };

  const MarketplaceTab = () => {
    return <MarketplaceBrowser />;
  };

  const TransferTab = () => {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Transfer Tokens</Typography>
        <Box display="flex" justifyContent="center" p={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setTransferModalOpen(true)}
            startIcon={<TransferIcon />}
          >
            Transfer Tokens
          </Button>
        </Box>
      </Paper>
    );
  };

  const EarnTab = () => {
    return <EarningOpportunities />;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            MembaCoin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your tokens, browse the marketplace, and track your transaction history
          </Typography>
        </Box>

        {error && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!error && (!tokenWalletService.getTransactions || !tokenWalletService.getHistoricalTokenTransactions || !tokenWalletService.exchangeTokens) && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Some token wallet features are still under development. Full functionality will be available soon.
          </Alert>
        )}

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={getTabIndex()}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab icon={<WalletIcon />} label="Wallet" />
            <Tab icon={<StoreIcon />} label="Marketplace" />
            <Tab icon={<HistoryIcon />} label="History" />
            <Tab icon={<TransferIcon />} label="Transfer" />
            <Tab icon={<EarnIcon />} label="Earn Tokens" />
          </Tabs>
        </Paper>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {currentTab === 'wallet' && <WalletTab />}
            {currentTab === 'marketplace' && <MarketplaceTab />}
            {currentTab === 'history' && <TransactionHistoryTab />}
            {currentTab === 'transfer' && <TransferTab />}
            {currentTab === 'earn' && <EarnTab />}
          </Box>
        )}

        {wallets.length > 0 && (
          <TransferTokensModal
            open={transferModalOpen}
            onClose={() => setTransferModalOpen(false)}
            onTransfer={handleTransfer}
            wallet={wallets[0]}
          />
        )}

        {wallets.length > 0 && (
          <ExchangeTokensModal
            open={exchangeModalOpen}
            onClose={() => setExchangeModalOpen(false)}
            onExchange={handleExchange}
            wallet={wallets[0]}
          />
        )}
      </Container>
    </Box>
  );
};

export default MemberSkillCoinDashboardPage;
